import React, { useState, useEffect } from 'react';
import { FiGlobe, FiSearch, FiCamera, FiInfo } from 'react-icons/fi';
import { getPassportCountries } from '../services/api';
import './PassportCountries.css';

/**
 * PassportCountries Component
 * Shows all available countries with their passport photo sizes
 */
const PassportCountries = () => {
  const [countries, setCountries] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState(null);

  // Fetch countries on mount with cleanup
  useEffect(() => {
    let isMounted = true;
    
    const fetchCountries = async () => {
      try {
        setLoading(true);
        const response = await getPassportCountries();
        if (isMounted) {
          setCountries(response?.countries || {});
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error loading countries:', err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchCountries();
    
    // Cleanup
    return () => {
      isMounted = false;
    };
  }, []);

  // Filter countries based on search
  const filteredCountries = Object.entries(countries)
    .filter(([name]) => 
      name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => a[0].localeCompare(b[0]));

  // Get total count
  const totalCount = Object.keys(countries).length;

  // Common passport sizes for quick reference
  const commonSizes = [
    { name: 'US Standard', width: 600, height: 600, desc: '2x2 inches' },
    { name: 'EU Standard', width: 413, height: 531, desc: '35x45mm' },
    { name: 'UK', width: 413, height: 531, desc: '35x45mm' },
    { name: 'India', width: 600, height: 600, desc: '2x2 inches (51x51mm)' },
    { name: 'China', width: 390, height: 567, desc: '33x48mm' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-2 text-gray-900 dark:text-white flex items-center gap-3">
          <FiGlobe className="text-blue-600" /> Passport Photo Sizes
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Country-specific passport photo dimensions for {totalCount} countries
        </p>
      </div>

      {/* Quick Reference */}
      <div className="mb-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
        {commonSizes.map((size) => (
          <div
            key={size.name}
            className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-4 border border-blue-100 dark:border-blue-800"
          >
            <p className="text-sm font-bold text-gray-900 dark:text-white">{size.name}</p>
            <p className="text-xs text-blue-600 font-semibold">{size.width}x{size.height} px</p>
            <p className="text-xs text-gray-500">{size.desc}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search country..."
            className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          )}
        </div>
        {searchTerm && (
          <p className="mt-2 text-sm text-gray-500">
            Found {filteredCountries.length} of {totalCount} countries
          </p>
        )}
      </div>

      {/* Countries Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredCountries.map(([name, size]) => (
            <button
              key={name}
              onClick={() => setSelectedCountry(selectedCountry === name ? null : name)}
              className={`text-left p-4 rounded-xl border-2 transition-all ${
                selectedCountry === name
                  ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-800 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 dark:text-white text-sm">
                    {name}
                  </h3>
                  <div className="mt-2 flex items-center gap-2">
                    <FiCamera className="text-blue-600" size={14} />
                    <span className="text-sm font-semibold text-blue-600">
                      {size.width} × {size.height} px
                    </span>
                  </div>
                </div>
              </div>

              {selectedCountry === name && (
                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <FiInfo size={12} />
                    <span>
                      Aspect ratio: {(size.width / size.height).toFixed(2)}
                    </span>
                  </div>
                  <div className="mt-2 h-16 bg-gray-100 dark:bg-slate-700 rounded-lg flex items-center justify-center relative overflow-hidden">
                    <div
                      className="bg-blue-200 dark:bg-blue-600/30 border-2 border-dashed border-blue-400 flex items-center justify-center"
                      style={{
                        width: `${Math.min(60, (size.width / size.height) * 50)}px`,
                        height: '50px',
                      }}
                    >
                      <span className="text-[8px] text-blue-600 font-bold">Photo</span>
                    </div>
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {!loading && filteredCountries.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 dark:bg-slate-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FiSearch className="text-3xl text-gray-400" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">No Countries Found</h3>
          <p className="text-gray-500 text-sm">
            Try searching with a different term
          </p>
        </div>
      )}
    </div>
  );
};

export default PassportCountries;
